
import React, {
    useEffect,
    useState
} from "react";

import {
    FaBookOpen,
    FaArrowLeft,
    FaArrowRight,
    FaDownload
} from "react-icons/fa";

import {
    Link
} from "react-router-dom";

import Header from "../components/Header";
import Footer from "../components/Footer";

import API_BASE_URL from "../config/config";

import "./FreeStudyMaterial.css";


const StudyMaterial = () => {

    // =========================================================
    // API
    // =========================================================

    const API_URL = API_BASE_URL.replace(/\/$/, "");

    const STORAGE_BASE_URL = API_URL.replace(/\/api$/, "");

    const IMAGE_STORAGE_URL =
        `${STORAGE_BASE_URL}/storage/study`;

    const PDF_STORAGE_URL =
        `${STORAGE_BASE_URL}/storage/jobs`;


    // =========================================================
    // STATES
    // =========================================================

    const [subjects, setSubjects] = useState([]);

    const [materials, setMaterials] = useState([]);

    const [selectedSubject, setSelectedSubject] =
        useState("");

    const [currentPage, setCurrentPage] =
        useState(1);

    const [lastPage, setLastPage] =
        useState(1);

    const [loadingSubjects, setLoadingSubjects] =
        useState(true);

    const [loadingMaterials, setLoadingMaterials] =
        useState(false);

    const [error, setError] =
        useState("");


    // =========================================================
    // GET IMAGE URL
    // =========================================================

    const getImageUrl = (image) => {

        if (!image) {
            return "";
        }

        if (
            image.startsWith("http://") ||
            image.startsWith("https://")
        ) {
            return image;
        }

        return `${IMAGE_STORAGE_URL}/${image.replace(/^\/+/, "")}`;
    };


    // =========================================================
    // GET PDF URL
    // =========================================================

    const getPdfUrl = (pdf) => {

        if (!pdf) {
            return "";
        }

        if (
            pdf.startsWith("http://") ||
            pdf.startsWith("https://")
        ) {
            return pdf;
        }

        return `${PDF_STORAGE_URL}/${pdf.replace(/^\/+/, "")}`;
    };


    // =========================================================
    // GET PLAIN TEXT
    // =========================================================

    const getPlainText = (html) => {

        if (!html) {
            return "";
        }

        const tempDiv =
            document.createElement("div");

        tempDiv.innerHTML = html;

        return (
            tempDiv.textContent ||
            tempDiv.innerText ||
            ""
        );
    };


    // =========================================================
    // FORMAT DATE
    // =========================================================

    const formatDate = (date) => {

        if (!date) {
            return "";
        }

        const dateObject =
            new Date(date);

        if (
            Number.isNaN(
                dateObject.getTime()
            )
        ) {
            return date;
        }

        return dateObject.toLocaleDateString(
            "en-US",
            {
                month: "long",
                day: "numeric",
                year: "numeric"
            }
        );
    };


    // =========================================================
    // FETCH SUBJECTS
    // =========================================================

    const fetchSubjects = async () => {

        try {

            setLoadingSubjects(true);
            setError("");

            const response =
                await fetch(
                    `${API_URL}/free-study-material/subjects`
                );

            const data =
                await response.json();

            console.log(
                "SUBJECTS:",
                data
            );

            if (
                response.ok &&
                data.success
            ) {

                setSubjects(
                    Array.isArray(data.data)
                        ? data.data
                        : []
                );

            } else {

                setSubjects([]);

                setError(
                    data.message ||
                    "Unable to load subjects."
                );
            }

        } catch (error) {

            console.error(
                "SUBJECT FETCH ERROR:",
                error
            );

            setSubjects([]);

            setError(
                "Unable to load subjects."
            );

        } finally {

            setLoadingSubjects(false);

        }
    };


    // =========================================================
    // FETCH MATERIALS
    // =========================================================

    const fetchMaterials = async (
        subject,
        page = 1
    ) => {

        try {

            setLoadingMaterials(true);
            setError("");

            const response =
                await fetch(
                    `${API_URL}/free-study-material?subject=${encodeURIComponent(
                        subject
                    )}&page=${page}`
                );

            const data =
                await response.json();

            console.log(
                "STUDY MATERIALS:",
                data
            );

            if (
                response.ok &&
                data.success
            ) {

                setMaterials(
                    Array.isArray(data.data)
                        ? data.data
                        : []
                );

                setCurrentPage(
                    data.pagination?.current_page ||
                    page
                );

                setLastPage(
                    data.pagination?.last_page ||
                    1
                );

            } else {

                setMaterials([]);

                setLastPage(1);

                setError(
                    data.message ||
                    "Unable to load study materials."
                );
            }

        } catch (error) {

            console.error(
                "MATERIAL FETCH ERROR:",
                error
            );

            setMaterials([]);

            setLastPage(1);

            setError(
                "Unable to load study materials."
            );

        } finally {

            setLoadingMaterials(false);

        }
    };


    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        fetchSubjects();

    }, []);


    // =========================================================
    // SELECT SUBJECT
    // =========================================================

    const handleSubjectClick = (subject) => {

        setSelectedSubject(subject);

        setCurrentPage(1);

        fetchMaterials(
            subject,
            1
        );

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    // =========================================================
    // BACK TO SUBJECTS
    // =========================================================

    const handleBackToSubjects = () => {

        setSelectedSubject("");

        setMaterials([]);

        setCurrentPage(1);

        setLastPage(1);

        setError("");

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    // =========================================================
    // PAGE CHANGE
    // =========================================================

    const handlePageChange = (page) => {

        if (
            page < 1 ||
            page > lastPage ||
            !selectedSubject
        ) {
            return;
        }

        setCurrentPage(page);

        fetchMaterials(
            selectedSubject,
            page
        );

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    // =========================================================
    // RENDER SUBJECTS
    // =========================================================

    const renderSubjects = () => {

        if (loadingSubjects) {

            return (
                <div className="study-loading">

                    <div
                        className="spinner-border text-primary"
                        role="status"
                    />

                    <p>
                        Loading subjects...
                    </p>

                </div>
            );
        }


        if (subjects.length === 0) {

            return (
                <div className="study-empty">

                    <h5>
                        No Subjects Found
                    </h5>

                    <p>
                        Study material is currently unavailable.
                    </p>

                </div>
            );
        }


        return (
            <div className="study-subject-grid">

                {subjects.map(
                    (subject, index) => (

                        <div
                            className={`study-subject-card bg-${(index % 3) + 1}`}
                            key={subject.subject}
                            onClick={() =>
                                handleSubjectClick(
                                    subject.subject
                                )
                            }
                        >

                            <div className="study-subject-icon">

                                <FaBookOpen />

                            </div>


                            <h5>

                                {subject.subject}

                            </h5>


                            <p>

                                Total Posts :{" "}

                                {subject.total_posts}

                            </p>

                        </div>
                    )
                )}

            </div>
        );
    };


    // =========================================================
    // RENDER MATERIAL
    // =========================================================

    const renderMaterial = (material) => {

        const plainDescription =
            getPlainText(
                material.content
            );

        const description =
            plainDescription.length > 80
                ? `${plainDescription.substring(
                    0,
                    80
                ).trim()}...`
                : plainDescription;


        return (

            <article
                className="study-material-card"
                key={material.study_id}
            >

                <div className="row g-0">


                    {/* IMAGE */}

                    <div className="col-md-6">

                        <div className="study-material-image-wrapper">

                            {material.image ? (

                                <img
                                    src={
                                        getImageUrl(
                                            material.image
                                        )
                                    }
                                    alt={
                                        material.title
                                    }
                                    className="study-material-image"
                                />

                            ) : (

                                <div className="study-no-image">

                                    <FaBookOpen />

                                    <span>
                                        Study Material
                                    </span>

                                </div>

                            )}


                            <div className="study-date-badge">

                                {formatDate(
                                    material.date_added
                                )}

                            </div>

                        </div>

                    </div>


                    {/* CONTENT */}

                    <div className="col-md-6">

                        <div className="study-material-content">

                            <div className="study-material-meta">

                                <span>
                                    Subject
                                </span>

                                <strong>
                                    {material.subject}
                                </strong>

                            </div>


                            <h4 className="study-material-title">

                                {material.title}

                            </h4>


                            <p className="study-material-description">

                                {description}

                            </p>


                            {/* ACTIONS */}

                            <div className="study-material-actions">


                                


                                {/* DOWNLOAD PDF */}

                                {material.download && (

                                    <a
                                        href={
                                            getPdfUrl(
                                                material.download
                                            )
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="study-download-btn"
                                    >

                                        <FaDownload />

                                        Download Content

                                    </a>

                                )}

                            </div>

                        </div>

                    </div>

                </div>

            </article>
        );
    };


    // =========================================================
    // PAGINATION
    // =========================================================

    const renderPagination = () => {

        if (lastPage <= 1) {
            return null;
        }


        return (

            <nav className="study-pagination">

                <ul className="pagination">


                    {/* PREVIOUS */}

                    <li
                        className={`page-item ${
                            currentPage === 1
                                ? "disabled"
                                : ""
                        }`}
                    >

                        <button
                            type="button"
                            className="page-link"
                            disabled={
                                currentPage === 1
                            }
                            onClick={() =>
                                handlePageChange(
                                    currentPage - 1
                                )
                            }
                        >

                            <FaArrowLeft />

                            Prev

                        </button>

                    </li>


                    {/* PAGE NUMBERS */}

                    {Array.from(
                        {
                            length: lastPage
                        },
                        (_, index) =>
                            index + 1
                    ).map(
                        (page) => (

                            <li
                                key={page}
                                className={`page-item ${
                                    currentPage === page
                                        ? "active"
                                        : ""
                                }`}
                            >

                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() =>
                                        handlePageChange(
                                            page
                                        )
                                    }
                                >

                                    {page}

                                </button>

                            </li>

                        )
                    )}


                    {/* NEXT */}

                    <li
                        className={`page-item ${
                            currentPage === lastPage
                                ? "disabled"
                                : ""
                        }`}
                    >

                        <button
                            type="button"
                            className="page-link"
                            disabled={
                                currentPage === lastPage
                            }
                            onClick={() =>
                                handlePageChange(
                                    currentPage + 1
                                )
                            }
                        >

                            Next

                            <FaArrowRight />

                        </button>

                    </li>

                </ul>

            </nav>
        );
    };


    // =========================================================
    // MAIN RENDER
    // =========================================================

    return (

        <>

            <Header />


            <section className="free-study-material-section">

                <div className="container">

                    <div className="row">


                        {/* =================================================
                            FULL WIDTH CONTENT
                        ================================================= */}

                        <div className="col-12">


                            {/* =================================================
                                SUBJECT LIST
                            ================================================= */}

                            {!selectedSubject ? (

                                <>

                                    <div className="study-section-heading">

                                        <span>
                                            Select A
                                        </span>

                                        <strong>
                                            Subject
                                        </strong>

                                    </div>


                                    {error && (

                                        <div className="alert alert-danger">

                                            {error}

                                        </div>

                                    )}


                                    {renderSubjects()}

                                </>

                            ) : (

                                <>


                                    {/* =================================================
                                        SUBJECT HEADER
                                    ================================================= */}

                                    <div className="study-section-heading study-selected-heading">

                                        <div>

                                            <span>
                                                Free Study
                                            </span>

                                            <strong>
                                                {selectedSubject}
                                            </strong>

                                        </div>


                                        <button
                                            type="button"
                                            className="study-back-btn"
                                            onClick={
                                                handleBackToSubjects
                                            }
                                        >

                                            <FaArrowLeft />

                                            All Subjects

                                        </button>

                                    </div>


                                    {/* ERROR */}

                                    {error && (

                                        <div className="alert alert-danger">

                                            {error}

                                        </div>

                                    )}


                                    {/* LOADING */}

                                    {loadingMaterials ? (

                                        <div className="study-loading">

                                            <div
                                                className="spinner-border text-primary"
                                                role="status"
                                            />

                                            <p>
                                                Loading study materials...
                                            </p>

                                        </div>

                                    ) : materials.length === 0 ? (

                                        <div className="study-empty">

                                            <h5>
                                                No Study Material Found
                                            </h5>

                                            <p>
                                                There are no posts available for this subject.
                                            </p>

                                        </div>

                                    ) : (

                                        <>

                                            {materials.map(
                                                renderMaterial
                                            )}

                                            {renderPagination()}

                                        </>

                                    )}

                                </>

                            )}

                        </div>

                    </div>

                </div>

            </section>


            <Footer />

        </>

    );
};


export default StudyMaterial;
